import { createPortal } from 'react-dom';
import { useState } from 'react';
import { SKILLS, EXTRAS } from '../data/skills';

const SKILL_NAME = Object.fromEntries(SKILLS.map(s => [s.id, s.name]));
const EXTRA_NAME = Object.fromEntries(EXTRAS.map(e => [e.id, e.name]));

export default function ContribTooltip({ data, onMouseEnter, onMouseLeave, onMoreInfo }) {
  const [activeTab, setActiveTab] = useState('bonuses');

  if (!data) return null;
  const { pos, skills, extras, passiveSkills, special, description, drops, title, spellbooks, prayerBooks, prayerUnlocks } = data;
  const skillEntries = Object.entries(skills || {}).filter(([, v]) => v > 0).sort(([, a], [, b]) => a - b);
  const extraEntries = Object.entries(extras || {}).filter(([, v]) => v > 0).sort(([, a], [, b]) => a - b);
  const clampedLeft = Math.min(pos.left, window.innerWidth - 230);

  const hasSpellbooks = spellbooks?.filter(b => b !== 'Standard').length > 0;
  const hasPrayerBooks = prayerBooks?.filter(b => b !== 'Standard').length > 0;
  const hasPrayerUnlocks = prayerUnlocks?.length > 0;
  const hasContribs = skillEntries.length > 0 || extraEntries.length > 0 || hasSpellbooks || hasPrayerBooks || hasPrayerUnlocks;
  const hasDrops = drops?.length > 0;
  const showTabs = hasContribs && hasDrops;
  const sortedDrops = hasDrops
    ? [...drops].sort((a, b) => (b.echo ? 1 : 0) - (a.echo ? 1 : 0))
    : [];

  return createPortal(
    <div
      className="contrib-tooltip"
      style={{ position: 'fixed', left: clampedLeft, top: pos.top }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {title && (
        <div className="contrib-title">{title}</div>
      )}
      {description && (
        <div className="contrib-description">{description}</div>
      )}
      {showTabs && (
        <div className="contrib-tabs">
          <button
            className={`contrib-tab ${activeTab === 'bonuses' ? 'active' : ''}`}
            onClick={() => setActiveTab('bonuses')}
          >Bonuses</button>
          <button
            className={`contrib-tab ${activeTab === 'gear' ? 'active' : ''}`}
            onClick={() => setActiveTab('gear')}
          >Gear</button>
        </div>
      )}
      <div className="contrib-scroll">
      {special === 'reloaded' ? (
        <div className="contrib-row" style={{ color: '#c8b896', fontStyle: 'italic' }}>
          Copies another relic's bonuses
        </div>
      ) : (
        <>
          {(!showTabs || activeTab === 'bonuses') && (
            <>
              {hasContribs ? (
                <>
                  {skillEntries.length > 0 && (
                    <div className="contrib-section">
                      {!showTabs && <div className="contrib-label">Bonuses</div>}
                      {skillEntries.map(([id, val]) => (
                        <div key={id} className="contrib-row">
                          <span>{SKILL_NAME[id] || id}</span>
                          <span className="contrib-val">+{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {passiveSkills && Object.entries(passiveSkills).filter(([, v]) => v > 0).length > 0 && (
                    <div className="contrib-section">
                      <div className="contrib-label">Tier Passives</div>
                      {Object.entries(passiveSkills).filter(([, v]) => v > 0).map(([id, val]) => (
                        <div key={id} className="contrib-row">
                          <span>{SKILL_NAME[id] || id}</span>
                          <span className="contrib-val">+{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {extraEntries.length > 0 && (
                    <div className="contrib-section">
                      <div className="contrib-label">Extras</div>
                      {extraEntries.map(([id, val]) => (
                        <div key={id} className="contrib-row">
                          <span>{EXTRA_NAME[id] || id}</span>
                          <span className="contrib-val">+{val}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {hasSpellbooks && (
                    <div className="contrib-section">
                      <div className="contrib-label">Spellbooks</div>
                      {spellbooks.filter(b => b !== 'Standard').map(b => (
                        <div key={b} className="contrib-row contrib-row-unlock">
                          <span>{b}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(hasPrayerBooks || hasPrayerUnlocks) && (
                    <div className="contrib-section">
                      <div className="contrib-label">Prayers</div>
                      {hasPrayerBooks && prayerBooks.filter(b => b !== 'Standard').map(b => (
                        <div key={b} className="contrib-row contrib-row-unlock">
                          <span>{b}</span>
                        </div>
                      ))}
                      {hasPrayerUnlocks && prayerUnlocks.map(p => (
                        <div key={p} className="contrib-row contrib-row-unlock">
                          <span>{p}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                !showTabs && !description && !hasDrops && (
                  <div className="contrib-row" style={{ color: '#7a6a4a', fontStyle: 'italic' }}>No skill contributions</div>
                )
              )}
            </>
          )}
          {(!showTabs || activeTab === 'gear') && hasDrops && (
            <div className="contrib-section">
              {!showTabs && <div className="contrib-label">Notable Gear</div>}
              {sortedDrops.map(d => (
                <div key={d.name} className={`contrib-row${d.echo ? ' contrib-row-echo' : ''}`}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    {d.echo && <span className="echo-badge">ECHO</span>}
                    {d.name}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      </div>
      {onMoreInfo && title && (
        <button className="contrib-more-btn" onClick={() => onMoreInfo(title)}>
          More info →
        </button>
      )}
    </div>,
    document.body
  );
}
